// components/RequestPasswordReset.tsx - パスワードリセットリクエスト画面

import React, { useState } from 'react';
import {
  Container,
  Paper,
  Typography,
  TextField,
  Button,
  Box,
  Alert,
  CircularProgress,
  Link,
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import axios from 'axios';
import { normalizeEmail } from '../utils/normalizeEmail';

const API_URL = process.env.REACT_APP_API_URL ?? '';

const RequestPasswordReset: React.FC = () => {
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const validateEmail = () => {
    const normalizedEmail = normalizeEmail(email);

    if (!normalizedEmail) {
      setError('メールアドレスを入力してください。');
      return false;
    }

    const emailRegex = /\S+@\S+\.[\S]+/;
    if (!emailRegex.test(normalizedEmail)) {
      setError('有効なメールアドレスを入力してください。');
      return false;
    }

    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess(false);

    if (!validateEmail()) {
      return;
    }

    setIsLoading(true);

    try {
      await axios.post(`${API_URL}/api/auth/request-password-reset`, { email: normalizeEmail(email) });
      setSuccess(true);
    } catch (err: unknown) {
      if (axios.isAxiosError(err)) {
        setError(err.response?.data?.detail || 'パスワードリセットリクエストに失敗しました');
      } else {
        setError('パスワードリセットリクエストに失敗しました');
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Container maxWidth="sm" sx={{ mt: 8 }}>
      <Paper elevation={3} sx={{ p: 4 }}>
        <Typography variant="h5" component="h1" align="center" gutterBottom>
          パスワードリセット
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        {success ? (
          <Box>
            <Alert severity="success" sx={{ mb: 3 }}>
              パスワードリセットのリンクをメールで送信しました。メールをご確認ください。
            </Alert>
            <Box sx={{ textAlign: 'center', mt: 2 }}>
              <Link component={RouterLink} to="/login" variant="body2">
                ログイン画面に戻る
              </Link>
            </Box>
          </Box>
        ) : (
          <Box component="form" onSubmit={handleSubmit} noValidate>
            <Typography variant="body1" sx={{ mb: 2 }}>
              アカウントに登録されているメールアドレスを入力してください。パスワードリセットのリンクをお送りします。
            </Typography>
            <TextField
              margin="normal"
              required
              fullWidth
              id="email"
              label="メールアドレス"
              name="email"
              autoComplete="email"
              autoFocus
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={isLoading}
              type="email"
            />

            <Button
              type="submit"
              fullWidth
              variant="contained"
              sx={{ mt: 3, mb: 2, py: 1.5 }}
              disabled={isLoading}
            >
              {isLoading ? (
                <CircularProgress size={24} sx={{ color: 'white' }} />
              ) : (
                'リセットリンクを送信'
              )}
            </Button>

            <Box sx={{ mt: 2, textAlign: 'center' }}>
              <Link component={RouterLink} to="/login" variant="body2">
                ログイン画面に戻る
              </Link>
            </Box>
          </Box>
        )}
      </Paper>
    </Container>
  );
};

export default RequestPasswordReset;
